/**
 * RoomShell — the reusable dark-space wrapper.
 *
 *   ambient + nebula backgrounds (per room colour)
 *   header:  small stone + title + subtitle + optional action pills
 *   main:    scrollable content
 *   footer:  optional (usually an input row)
 *
 * Everything is `pointer-events:none` behind the fold so no light layer
 * intercepts clicks.
 */
import { useEffect } from "react";
import {
  BG, INK, MUTED_60, MUTED_36, FONT, ROOMS,
  YUO_KEYFRAMES, ambientBackground, nebulaBackground,
} from "@/lib/yuoDesign";
import StoneOrb from "@/components/yuo/StoneOrb";
import ThemeToggle from "@/components/ThemeToggle";
import { useState } from "react";
import { useResponsive } from "@/lib/responsive";
import { MobileHeader } from "@/components/mobile/MobileHeader";
import MoreSheet from "@/components/mobile/MoreSheet";

let __yuoStylesInjected = false;

function YuoStyles() {
  useEffect(() => {
    if (__yuoStylesInjected) return;
    __yuoStylesInjected = true;
    const s = document.createElement("style");
    s.setAttribute("data-yuo-styles", "1");
    s.textContent = YUO_KEYFRAMES;
    document.head.appendChild(s);
  }, []);
  return null;
}

export default function RoomShell({
  room = "mirror",
  title,
  subtitle,
  actions,
  children,
  footer,
  ambient,     // override background
  nebula,      // override nebula
  hideHeaderStone = false,
  testId,
  mobileActions,   // ≤2 working buttons for the phone header
  moreRows,        // rows for the ⋯ sheet on the phone
  moreTitle,
  showThemeToggle = true,
  subheader,       // fixed row under the header (never scrolls with the feed)
}) {
  const meta = ROOMS[room] || ROOMS.mirror;
  const amb = ambient || ambientBackground(meta.rgb);
  const neb = nebula || nebulaBackground(meta.rgb);
  const { isPhone } = useResponsive();
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <div data-testid={testId}
      style={{
        height: "100%", position: "relative",
        display: "flex", flexDirection: "column",
        background: BG, color: INK, fontFamily: FONT, overflow: "hidden",
      }}>
      <YuoStyles />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: amb }} />
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: neb, animation: "yuo-drift 34s ease-in-out infinite",
      }} />

      {isPhone && (title || actions) && (
        <MobileHeader room={room} title={title} sub={subtitle}
          actions={<>{showThemeToggle && <ThemeToggle compact />}{mobileActions ?? actions}</>}
          onMore={moreRows?.length ? () => setMoreOpen(true) : undefined} />
      )}
      {isPhone && moreRows?.length > 0 && (
        <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={moreTitle || `${title} · more`} rows={moreRows} />
      )}
      {!isPhone && (title || actions) && (
        <div style={{
          position: "relative", flex: "0 0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 24px 8px", gap: 14,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            {!hideHeaderStone && (
              <StoneOrb color={room} size={12} />
            )}
            {title && (
              <div style={{
                fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase",
                color: INK, whiteSpace: "nowrap",
              }}>{title}</div>
            )}
            {subtitle && (
              <div style={{
                fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
                color: MUTED_36, whiteSpace: "nowrap",
              }}>· {subtitle}</div>
            )}
          </div>
          {(actions || title) && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <ThemeToggle />
              {actions}
            </div>
          )}
        </div>
      )}

      {subheader && (
        <div data-testid="room-subheader" style={{ position: "relative", flex: "0 0 auto", padding: isPhone ? "0 14px" : "0 24px" }}>
          <div style={{ maxWidth: isPhone ? "none" : 1060, margin: "0 auto" }}>{subheader}</div>
        </div>
      )}

      <main className={isPhone ? "m-scroll" : undefined} style={{
        position: "relative", flex: "1 1 0", minHeight: 0, overflow: "auto",
        padding: isPhone ? "6px 14px 16px" : "10px 24px 20px",
      }}>
        {children}
      </main>

      {footer && (
        <div style={{ position: "relative", flex: "0 0 auto" }}>
          {footer}
        </div>
      )}
    </div>
  );
}
